import { useDeletePartnership } from '@/features/suppliers/hooks/partnerships'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { Alert, Button, GenericDialog } from '@/common'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

export default function DeletePartnershipDialog({
  isOpen,
  close,
  partnershipName,
  partnershipId,
  onAfterLeave
}: {
  isOpen: boolean
  close: () => void
  partnershipName: string
  partnershipId: string
  onAfterLeave?: () => void
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: deletePartnership, isPending: isDeleting } = useDeletePartnership(
    organizationId,
    partnershipId
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onDeleteClick = useCallback(async () => {
    try {
      setErrorMessage(null)
      await deletePartnership()
      toast.success(t('suppliers:suppliersPage.dialogs.deletePartnership.notifications.success'))
      close()
      if (onAfterLeave) {
        onAfterLeave()
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.message

      if (errorCode === 'PARTNERSHIP_HAS_IMPORTED_GOODS') {
        setErrorMessage(
          t('suppliers:suppliersPage.dialogs.deletePartnership.errors.partnershipHasImportedGoods')
        )
      } else {
        toast.error(t('suppliers:suppliersPage.dialogs.deletePartnership.notifications.error'))
      }
    }
  }, [deletePartnership, close, t, onAfterLeave])

  const onClose = useCallback(() => {
    close()
    setErrorMessage(null)
  }, [close])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('suppliers:suppliersPage.dialogs.deletePartnership.title')}
      size="md"
    >
      <div className="space-y-4">
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <p>
          <Trans
            i18nKey="suppliers:suppliersPage.dialogs.deletePartnership.description"
            values={{ name: partnershipName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
